import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface CursorFlowStep {
  /** Element selector to move the cursor to. */
  selector?: string;
  /** Move relative to this element's rect center instead of `selector` (e.g. map coordinates). */
  originSelector?: string;
  /** Offset in px applied to the resolved anchor (element center, or origin rect center). */
  offset?: { x: number; y: number };
  /** Action to perform on arrival. `type` writes `text` into the (input) element. */
  action?: 'click' | 'type' | 'finish';
  /** Text to type when `action: 'type'`. */
  text?: string;
  /** Dispatch a real DOM click on the resolved element (default: visual pulse only). */
  realClick?: boolean;
  /** Draw a growing circle from the previous point to this one (simulates the radius drag). */
  dragCircle?: boolean;
  /** Move the cursor itself in a circular path around the resolved anchor (e.g. to circle a cell). */
  orbit?: { radius: number; turns?: number };
  /** Tooltip shown while this step runs. */
  label?: string;
  /** Dwell time (ms) after the step completes, before the next one. */
  holdMs?: number;
}

interface CursorFlowState {
  /** Ordered steps to play, or null when no flow is running. */
  steps: CursorFlowStep[] | null;
  start: (steps: CursorFlowStep[]) => void;
  stop: () => void;
}

/**
 * App-wide guided "cursor flow": plays a choreographed sequence of pointer movements, fake clicks,
 * typing and a simulated circle-draw to demonstrate a task on the page the user was sent to
 * (e.g. after a Basic Value onboarding step that can't be completed in the modal).
 */
const useCursorFlowStore = create<CursorFlowState, [['zustand/devtools', never]]>(
  devtools(
    (set) => ({
      steps: null,
      start: (steps) => set({ steps }, false, 'cursorFlow/start'),
      stop: () => set({ steps: null }, false, 'cursorFlow/stop'),
    }),
    { name: 'useCursorFlowStore' }
  )
);

export default useCursorFlowStore;
